// Org-scoped programme options (org_programmes) — small per-org list loaded
// once and filtered client-side (v3 `searchApi={false}` parity). Moved from
// pages/groups/useGroupOptions (extract-on-second-consumer: groups + users,
// filters + forms). NOT the global programmes catalog — see useProgrammesSelect.
import { CrudFilter, useList } from '@refinedev/core';
import { useMemo } from 'react';

import type { APMultiSelectOption } from '@/components/ap/form';
import { attrs, type JsonApiRow } from './jsonApi';

/** The programme CODE rides beside value + label for callers that match a
 *  programme by code (codes are stable across databases, ids are not). */
export interface OrgProgrammeOption extends APMultiSelectOption {
  code?: string;
}

export const useOrgProgrammeSelect = ({
  orgId,
  forFilter = false,
  disableMB = false,
  valueField = 'id',
  hiddenProgrammeCodes = [],
  allowedOrgProgrammeIds = [],
  enabled = true,
}: {
  /** Superadmin filter context only — non-superadmins are scoped server-side. */
  orgId?: string | number;
  /** Filter-panel variant sends the v3 `should_serialize=false` + `by_org_id`. */
  forFilter?: boolean;
  /** v3 `disableMBIntegratedProgrammes` — MB-managed programmes not selectable. */
  disableMB?: boolean;
  /** Option value: org_programme PK (default) or the GLOBAL `programme_id`
   *  (v3 library-modal Program picker binds the global id). */
  valueField?: 'id' | 'programme_id';
  /** Drop programmes whose code matches (v3 hiddenProgrammeCodes — e.g. hide
   *  MYP from a non-MYP experience's Program picker). */
  hiddenProgrammeCodes?: string[];
  /** Keep only these org_programme PKs (v3 allowedOrgProgrammeIds — MYP lock). */
  allowedOrgProgrammeIds?: Array<string | number>;
  enabled?: boolean;
}) => {
  const filters: CrudFilter[] = forFilter
    ? [
        { field: 'should_serialize', operator: 'eq', value: false },
        ...(orgId ? [{ field: 'by_org_id', operator: 'eq', value: orgId } as CrudFilter] : []),
      ]
    : [];

  const { data, isLoading } = useList({
    resource: 'org_programmes',
    filters,
    pagination: { pageSize: 25 },
    queryOptions: { enabled },
  });

  // Content-keyed so callers can pass array literals without churning the memo.
  const codesKey = hiddenProgrammeCodes.join(',');
  const allowedKey = allowedOrgProgrammeIds.join(',');
  const options = useMemo<OrgProgrammeOption[]>(() => {
    const codes = codesKey ? codesKey.split(',') : [];
    const allowed = allowedKey ? allowedKey.split(',') : [];
    return (data?.data ?? [])
      .map((row: JsonApiRow) => {
        const item = attrs(row);
        // JSON:API rows may carry id outside `attributes` (original behavior).
        const pk = item.id ?? (row.id as string | number);
        return { item, pk, value: valueField === 'id' ? pk : item.programme_id };
      })
      .filter(({ pk }) => allowed.length === 0 || allowed.includes(String(pk)))
      .filter(({ item }) => codes.length === 0 || !codes.includes(item.code))
      .filter(({ value }) => value !== null && value !== undefined)
      .map(({ item, pk, value }) => ({
        value: value as string | number,
        label: String(item.name ?? pk),
        code: item.code as string | undefined,
        disabled: disableMB && item.settings?.mb_integration_enabled !== false,
      }));
  }, [data?.data, disableMB, valueField, codesKey, allowedKey]);

  return { options, isLoading };
};
